import { useState } from "react";
import {
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldError,
  FieldHint,
  FieldLabel,
  Overline,
  ReasonField,
  Select,
  apiPost,
  cx,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import { passageCheckResultSchema, type EvidenceRecord, type TurnView } from "../../contracts/index";
import { paragraphLabel } from "./DocumentView";
import { evidenceKeys } from "../keys";
import { clock } from "../format";

type Dimension = "wording" | "locator" | "context";

const CHECK_REASON = { minLength: 1, message: "Enter a reason" };

const MARK_SUFFIX = {
  verified: "verified",
  failed: "check failed",
  rechecking: "rechecking",
  unverified: "not verified",
} as const;

function Checkbox({
  label,
  hint,
  checked,
  onChange,
  disabled,
  title,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <label
      title={title}
      className={cx(
        "flex items-start gap-2 rounded-lg border border-divider px-2.5 py-1.5 text-md",
        disabled ? "cursor-not-allowed text-muted" : "cursor-pointer hover:bg-surface-2",
      )}
    >
      <input
        type="checkbox"
        className="mt-[3px] accent-[var(--color-link)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <span>
        <span className="font-strong">{label}</span>
        <span className="block text-muted">{hint}</span>
      </span>
    </label>
  );
}

function Estimate({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted">{label}</div>
      <div className="font-strong tabular-nums">{value}</div>
    </div>
  );
}

interface CheckDimensionsProps {
  isText: boolean;
  dims: Record<Dimension, boolean>;
  setDims: (dims: Record<Dimension, boolean>) => void;
  manual: boolean;
  setManual: (v: boolean) => void;
  manualOption: EvidenceRecord["check_options"]["manual_review"];
  checksError: string | null;
}

function CheckDimensions({
  isText,
  dims,
  setDims,
  manual,
  setManual,
  manualOption,
  checksError,
}: CheckDimensionsProps) {
  return (
    <fieldset className="m-0 mt-3 min-w-0 border-0 p-0" aria-describedby={checksError ? "check-dims-error" : undefined}>
      <Overline as="legend" spacing="narrow" className="mb-1.5 flex items-center gap-1.5 p-0">
        Checks <span className="font-medium tracking-normal text-attention normal-case">required</span>
      </Overline>
      <div className="grid grid-cols-2 gap-1.5 max-[560px]:grid-cols-1">
        <Checkbox
          label="Wording"
          hint={`Against the archived ${isText ? "page text" : "audio"}`}
          checked={dims.wording}
          onChange={(v) => {
            setDims({ ...dims, wording: v });
          }}
        />
        <Checkbox
          label="Position"
          hint={isText ? "Paragraph and column in the captured version" : "Start and end in the archived copy"}
          checked={dims.locator}
          onChange={(v) => {
            setDims({ ...dims, locator: v });
          }}
        />
        <Checkbox
          label="Context"
          hint="The question or remark it answers is included"
          checked={dims.context}
          onChange={(v) => {
            setDims({ ...dims, context: v });
          }}
        />
        <Checkbox
          label="Manual passage review"
          hint={manualOption.enabled ? "A person checks it; usually within 48 h. Creates a task." : manualOption.reason}
          checked={manual}
          disabled={!manualOption.enabled}
          onChange={setManual}
        />
      </div>
      {checksError ? <FieldError id="check-dims-error">{checksError}</FieldError> : null}
    </fieldset>
  );
}

function CheckPreview({
  chosen,
  estimateUsd,
  manual,
  checkedBefore,
}: {
  chosen: number;
  estimateUsd: EvidenceRecord["check_options"]["estimate_usd"];
  manual: boolean;
  checkedBefore: boolean;
}) {
  return (
    <div className="mt-3">
      <Overline as="h3" spacing="narrow" className="m-0 mb-1.5">
        Preview
      </Overline>
      <div aria-live="polite" className="rounded-lg border border-divider bg-surface px-3 py-2.5 text-md">
        <div className="grid grid-cols-4 gap-3 max-[560px]:grid-cols-2">
          <Estimate label="Automatic checks" value={String(chosen)} />
          <Estimate label="Estimated time" value={chosen > 0 ? "about 1 min" : "—"} />
          <Estimate label="Estimated cost" value={chosen > 0 ? `USD ${estimateUsd}` : "—"} />
          <Estimate label="Manual review" value={manual ? "≤ 48 h" : "No"} />
        </div>
        <p className="m-0 mt-2 text-muted">
          {checkedBefore
            ? "This passage was checked before; the new results replace the old ones and both stay in History. "
            : ""}
          {manual ? "A manual review task appears in Tasks for editors. " : ""}
          Readiness is re-evaluated when the checks finish.
        </p>
      </div>
    </div>
  );
}

export function RequestCheckDialog({
  record,
  initial,
  onClose,
}: {
  record: EvidenceRecord;
  initial: TurnView | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const isText = record.document !== null;
  const checkable = record.turns.filter((t) => t.unit_id !== null);
  const start = (initial?.unit_id ? initial : null) ?? checkable[0] ?? null;
  const turns = checkable.filter((t) => t.communication === start?.communication);
  const [unit, setUnit] = useState(start?.unit_id ?? "");
  const [dims, setDims] = useState<Record<Dimension, boolean>>({ wording: true, locator: true, context: true });
  const [manual, setManual] = useState(false);
  const reason = useReason(null, CHECK_REASON);
  const turn = turns.find((t) => t.unit_id === unit) ?? null;
  const previous = turn ? turns[turns.indexOf(turn) - 1] : undefined;
  const where = (t: TurnView) => (isText ? (paragraphLabel(t) ?? `Contribution ${String(t.n)}`) : clock(t.start_ms));
  const speaker = (t: TurnView) =>
    t.mention ?? record.speakers.find((s) => s.id === t.speaker_id)?.display ?? "Unattributed";
  const label = (t: TurnView) =>
    `${isText ? where(t) : `${clock(t.start_ms)}–${clock(t.end_ms)}`} · ${speaker(t)}${t.check ? ` · ${MARK_SUFFIX[t.check.mark]}` : ""}`;
  const chosen = (Object.keys(dims) as Dimension[]).filter((d) => dims[d]);
  const suspended = record.check_options.suspended;
  const mutation = useCommandMutation({
    run: (csrf) =>
      apiPost(
        `/api/evidence/items/${record.item.id}/passage-checks`,
        { unit_ids: [unit], dimensions: chosen, manual_review: manual, reason: reason.trimmed },
        passageCheckResultSchema,
        csrf,
      ),
    invalidate: [evidenceKeys.records],
    onSuccess: (result) => {
      toast.show(
        `${result.reused ? "Check already running or done" : "Check requested"} for ${turn ? where(turn) : "the passage"}.${manual ? " A manual review task was added to Tasks." : ""}`,
      );
      onClose();
    },
  });
  const checksError = reason.submitted && chosen.length === 0 ? "Choose at least one check" : null;
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Request passage check"
      onSubmit={() => {
        if (!reason.submit() || suspended || !turn || chosen.length === 0) return;
        mutation.mutate();
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm="Request check"
          disabledReason={suspended}
          pending={mutation.isPending}
        />
      }
    >
      <DialogText>
        Checks confirm that the wording, position and context were captured faithfully from the archived{" "}
        {isText ? "page" : "copy"}. They don&apos;t rate whether a statement is true.
      </DialogText>
      {suspended ? (
        <Callout tone="waiting" role="status" className="mb-2">
          {suspended}
        </Callout>
      ) : null}
      <div className="mt-3">
        <FieldLabel htmlFor="check-passage">Passage</FieldLabel>
        <Select
          id="check-passage"
          aria-describedby="check-context"
          value={unit}
          onChange={(e) => {
            setUnit(e.target.value);
          }}
        >
          {turns.map((t) => (
            <option key={t.unit_id} value={t.unit_id ?? ""}>
              {label(t)}
            </option>
          ))}
        </Select>
        <FieldHint id="check-context">
          {previous
            ? `Context included: ${where(previous)} (${speaker(previous)}).`
            : "First passage: no earlier context."}
        </FieldHint>
      </div>
      <CheckDimensions
        isText={isText}
        dims={dims}
        setDims={setDims}
        manual={manual}
        setManual={setManual}
        manualOption={record.check_options.manual_review}
        checksError={checksError}
      />
      <CheckPreview
        chosen={chosen.length}
        estimateUsd={record.check_options.estimate_usd}
        manual={manual}
        checkedBefore={Boolean(turn?.check)}
      />
      <ReasonField
        value={reason.reason}
        onChange={reason.setReason}
        error={reason.error}
        placeholder="Why does this passage need a check?"
      />
      <FailedCallout error={mutation.error} fallback="Couldn't request the check" />
    </Dialog>
  );
}
